import { Fragment } from "react";
import { Text } from "@foris/avocado-suite";
import { useTranslation } from "react-i18next";
import type { CurriculumTeachingDatesDisplay, PackageDetailsValues } from "../../types";
import css from "./packageConfigurationSummary.module.scss";

interface PackageConfigurationSummaryProps {
  packageDetails: PackageDetailsValues;
  teachingDates?: CurriculumTeachingDatesDisplay;
}

const PackageConfigurationSummary = ({
  packageDetails,
  teachingDates,
}: PackageConfigurationSummaryProps) => {
  const { t } = useTranslation();
  const { population } = packageDetails;

  if (!population) return null;

  const baseItems = [
    { key: "period", label: t("package-wizard.details-summary.period"), value: population.period },
    { key: "campus", label: t("package-wizard.details-summary.campus"), value: population.campus },
    {
      key: "modality",
      label: t("package-wizard.details-summary.modality"),
      value: population.modality,
    },
    { key: "level", label: t("package-wizard.details-summary.level"), value: population.level },
    {
      key: "program",
      label: t("package-wizard.details-summary.program"),
      value: population.program,
    },
    {
      key: "curriculum",
      label: t("package-wizard.details-summary.curriculum"),
      value: population.curriculum,
    },
    {
      key: "package",
      label: t("package-wizard.details-summary.package"),
      value: population.index,
    },
    {
      key: "package-name",
      label: t("package-wizard.details-summary.package-name"),
      value: packageDetails.packageName,
    },
  ];

  const teachingDateItems = [
    {
      key: "teaching-start-date",
      label: t("package-wizard.details-summary.teaching-start-date"),
      value: teachingDates?.start,
    },
    {
      key: "teaching-end-date",
      label: t("package-wizard.details-summary.teaching-end-date"),
      value: teachingDates?.end,
    },
  ].filter(item => item.value !== undefined);

  const items = [...baseItems, ...teachingDateItems];

  return (
    <div
      className={css.packageConfigurationSummary}
      aria-label={t("package-wizard.details-summary.label")}
    >
      {items.map((item, index) => (
        <Fragment key={item.key}>
          <div className={css.packageConfigurationSummary_item}>
            <Text type="sm">{item.label}:</Text>
            <Text type="sm" weight="medium">
              {item.value}
            </Text>
          </div>
          {index < items.length - 1 && (
            <span className={css.packageConfigurationSummary_divider} aria-hidden="true" />
          )}
        </Fragment>
      ))}
    </div>
  );
};

export default PackageConfigurationSummary;
